import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchCallCardFields } from '../api';
import {
  EMPTY_CALL_CARD_FIELDS,
  isFieldVisible,
  normalizeCallCardFields,
  resolveFieldTitle
} from '../components/calls/callCardFields';

const defaultValue = {
  fields: EMPTY_CALL_CARD_FIELDS.fields,
  candidates: EMPTY_CALL_CARD_FIELDS.candidates,
  title: (slot) => resolveFieldTitle(EMPTY_CALL_CARD_FIELDS, slot),
  isVisible: (slot) => isFieldVisible(EMPTY_CALL_CARD_FIELDS, slot),
  refresh: async () => {}
};

const CallCardFieldsContext = createContext(defaultValue);

/**
 * Названия и видимость полей «Услуга» и «Итог звонка». Карточка звонка встроена в четыре
 * раздела (журнал, «Клиенты», «Жалобы», примеры графиков), поэтому настройка живёт в контексте
 * над всеми, а не в каждом из них.
 *
 * Контекст, а не модульный кэш, как у каталога нод: экран настроек передаёт ответ PUT в
 * `refresh(known)`, и журнал на соседней вкладке меняет заголовок колонки сразу, без TTL.
 *
 * Запрос уходит только после логина (до него `GET` ответил бы 401). Ошибка чтения не гасит
 * карточку: остаются умолчания — оба поля видимы под своими прежними названиями.
 */
const CallCardFieldsProvider = ({ isAuthenticated = false, children }) => {
  const [state, setState] = useState(EMPTY_CALL_CARD_FIELDS);

  const refresh = useCallback(
    async (known) => {
      if (!isAuthenticated) {
        setState(EMPTY_CALL_CARD_FIELDS);
        return;
      }

      // Эндпоинты записи возвращают состояние целиком — второго запроса не нужно.
      if (known !== undefined) {
        setState(normalizeCallCardFields(known));
        return;
      }

      try {
        setState(normalizeCallCardFields(await fetchCallCardFields()));
      } catch (error) {
        console.warn('Failed to load call card field settings.', error);
      }
    },
    [isAuthenticated]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      fields: state.fields,
      candidates: state.candidates,
      title: (slot) => resolveFieldTitle(state, slot),
      isVisible: (slot) => isFieldVisible(state, slot),
      refresh
    }),
    [state, refresh]
  );

  return <CallCardFieldsContext.Provider value={value}>{children}</CallCardFieldsContext.Provider>;
};

const useCallCardFields = () => useContext(CallCardFieldsContext);

export { CallCardFieldsContext, CallCardFieldsProvider, useCallCardFields };
